// Payments data access. Claims, payers and import batches live in the shared
// backend (the /payments routes of the notes API), so every administrator sees
// the same payment log. Pages call subscribe() to reload after any write made
// from this browser; other admins' changes appear on the next load.
import {
  listPayers as apiListPayers, upsertPayer as apiUpsertPayer, deletePayer as apiDeletePayer,
  listClaims as apiListClaims, createClaim as apiCreateClaim, updateClaim as apiUpdateClaim, deleteClaim as apiDeleteClaim,
  deleteAllClaims as apiDeleteAllClaims, previewClaimImport, commitClaimImport, listClaimImports, rollbackClaimImport,
} from './api';
import { deriveClaim, normalizeStatus, summarize } from './claimMath';

const listeners = new Set();
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function emit() { listeners.forEach(fn => { try { fn(); } catch (e) { /* ignore */ } }); }

function hydrate(raw) {
  return deriveClaim({ ...raw, status: normalizeStatus(raw.status) });
}

// Several pages ask for the claim list at the same moment (dashboard, client
// record, payment log). One in-flight request serves all of them.
let claimsPromise = null;
function fetchClaims() {
  if (!claimsPromise) {
    claimsPromise = apiListClaims()
      .then(r => (r.claims || []).map(hydrate))
      .finally(() => { claimsPromise = null; });
  }
  return claimsPromise;
}

export const paymentsApi = {
  async listPayers() { const r = await apiListPayers(); return r.payers || []; },
  async upsertPayer(payer) {
    const r = await apiUpsertPayer({
      payerId: payer.payerId, name: payer.name, shortName: payer.shortName || '',
      aliases: payer.aliases || [], active: payer.active !== false,
    });
    emit(); return r.payer;
  },
  async deletePayer(payerId) { await apiDeletePayer(payerId); emit(); return { ok: true }; },

  async listClaims(filters = {}) {
    let rows = await fetchClaims();
    if (filters.payerId) rows = rows.filter(c => c.payerId === filters.payerId);
    if (filters.status) rows = rows.filter(c => c.status === filters.status);
    if (filters.clientId) rows = rows.filter(c => c.clientId === filters.clientId);
    if (filters.month) rows = rows.filter(c => c.serviceMonth === filters.month);
    if (filters.from) rows = rows.filter(c => c.dosStart >= filters.from);
    if (filters.to) rows = rows.filter(c => c.dosStart <= filters.to);
    return [...rows].sort((a, b) => (b.dosStart || '').localeCompare(a.dosStart || ''));
  },
  async getClaim(claimId) { const rows = await fetchClaims(); return rows.find(c => c.claimId === claimId) || null; },
  async createClaim(input) { const r = await apiCreateClaim(input); emit(); return hydrate(r.claim); },
  async updateClaim(claimId, input) { const r = await apiUpdateClaim(claimId, input); emit(); return hydrate(r.claim); },
  async deleteClaim(claimId) { await apiDeleteClaim(claimId); emit(); return { ok: true }; },

  async getSummary(filters = {}) {
    const rows = await this.listClaims(filters);
    return summarize(rows, { months: filters.months || 12 });
  },

  // rows: the mapped spreadsheet rows (same field names as createClaim input).
  // The backend matches payers and client records and checks for existing claims.
  async importPreview(rows) { return previewClaimImport(rows); },
  async importCommit(rows, { mode = 'update', fileName = '' } = {}) {
    const r = await commitClaimImport(rows, { mode, fileName });
    emit(); return r;
  },
  async listImports() { const r = await listClaimImports(); return r.imports || []; },
  async rollbackImport(batchId) { const r = await rollbackClaimImport(batchId); emit(); return r; },

  async clearClaims() { const r = await apiDeleteAllClaims(); emit(); return r; },
  async stats() {
    const [claims, payers, imports] = await Promise.all([fetchClaims(), this.listPayers(), this.listImports()]);
    return { claims: claims.length, payers: payers.length, imports: imports.length };
  },
};
